import * as THREE from 'three';

/**
 * Super Shield - Ultimate two-handed spell (both palms held steady).
 * Double-strength shield with mirror-like surface that reflects 30% damage
 * back to attacker. Lasts 4-5 seconds. Larger and brighter than normal shield.
 */
export class SuperShield {
  private mesh!: THREE.Group;
  private dome!: THREE.Mesh;
  private mirrorDome!: THREE.Mesh;
  private runes!: THREE.Points;
  private scene: THREE.Scene;
  private isActive: boolean = true;
  private duration: number;
  private startTime: number;
  private health: number;
  private maxHealth: number;
  private reflectFraction: number;
  private hitEffectTimeout: ReturnType<typeof setTimeout> | null = null;
  private rippleAnimationId: number | null = null;
  private dissolveAnimationId: number | null = null;

  constructor(
    scene: THREE.Scene,
    position: THREE.Vector3,
    health: number,
    duration: number,
    reflectFraction: number = 0.3,
  ) {
    this.scene = scene;
    this.health = health;
    this.maxHealth = health;
    this.duration = duration;
    this.reflectFraction = reflectFraction;
    this.startTime = Date.now();

    this.mesh = new THREE.Group();
    this.mesh.position.copy(position);

    this.createDome();
    this.createMirrorDome();
    this.createRunes();
    this.createInnerGlow();
    this.createEnergyRings();

    this.scene.add(this.mesh);
  }

  private createDome(): void {
    // Larger dome than normal shield (radius 2.2 vs 1.5)
    const geometry = new THREE.SphereGeometry(2.2, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });
    this.dome = new THREE.Mesh(geometry, material);
    this.dome.rotation.x = Math.PI;
    this.mesh.add(this.dome);

    // Wireframe overlay
    const wireGeometry = new THREE.SphereGeometry(2.22, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFEE88,
      transparent: true,
      opacity: 0.6,
      wireframe: true,
    });
    const wireframe = new THREE.Mesh(wireGeometry, wireMaterial);
    wireframe.rotation.x = Math.PI;
    this.mesh.add(wireframe);
  }

  private createMirrorDome(): void {
    // Inner mirror-like reflective dome
    const geometry = new THREE.SphereGeometry(2.0, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: 0xFFFFFF,
      transparent: true,
      opacity: 0.15,
      side: THREE.DoubleSide,
    });
    this.mirrorDome = new THREE.Mesh(geometry, material);
    this.mirrorDome.rotation.x = Math.PI;
    this.mesh.add(this.mirrorDome);
  }

  private createRunes(): void {
    const runeCount = 50; // More runes than normal shield
    const positions = new Float32Array(runeCount * 3);
    const colors = new Float32Array(runeCount * 3);

    for (let i = 0; i < runeCount; i++) {
      const theta = (i / runeCount) * Math.PI * 2;
      const phi = Math.random() * Math.PI / 2;
      const r = 2.1;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.cos(phi);
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);

      // Golden-white rune colors
      colors[i * 3] = 1;
      colors[i * 3 + 1] = 0.85 + Math.random() * 0.15;
      colors[i * 3 + 2] = 0.3 + Math.random() * 0.4;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
    });

    this.runes = new THREE.Points(geometry, material);
    this.mesh.add(this.runes);
  }

  private createInnerGlow(): void {
    const glowGeometry = new THREE.SphereGeometry(1.2, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.2,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.y = 0.5;
    this.mesh.add(glow);
  }

  private createEnergyRings(): void {
    // Orbiting energy rings for the "ultimate" feel
    for (let i = 0; i < 3; i++) {
      const ringGeo = new THREE.TorusGeometry(2.0 + i * 0.15, 0.03, 8, 32);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xFFDD44,
        transparent: true,
        opacity: 0.5 - i * 0.1,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = Math.PI / 2 + i * 0.3;
      ring.rotation.z = i * Math.PI / 3;
      ring.name = `energyRing${i}`;
      this.mesh.add(ring);
    }
  }

  update(delta: number): boolean {
    if (!this.isActive) return false;

    const elapsed = Date.now() - this.startTime;
    if (elapsed >= this.duration) {
      this.deactivate();
      return false;
    }

    // Rotate runes
    if (this.runes && this.runes.geometry.attributes.position) {
      this.runes.rotation.y += delta * 0.7;

      const positions = this.runes.geometry.attributes.position.array as Float32Array;
      const runeCount = positions.length / 3;
      for (let i = 0; i < runeCount; i++) {
        const baseY = positions[i * 3 + 1];
        positions[i * 3 + 1] = baseY + Math.sin(Date.now() * 0.003 + i) * 0.01;
      }
      this.runes.geometry.attributes.position.needsUpdate = true;
    }

    // Health-based opacity
    if (this.dome) {
      const healthRatio = this.health / this.maxHealth;
      const domeMaterial = this.dome.material as THREE.MeshBasicMaterial;
      domeMaterial.opacity = 0.3 + healthRatio * 0.4;

      const pulseIntensity = 1 + Math.sin(Date.now() * 0.005) * 0.05;
      this.dome.scale.setScalar(pulseIntensity);
    }

    // Mirror shimmer
    if (this.mirrorDome) {
      const mirrorMat = this.mirrorDome.material as THREE.MeshBasicMaterial;
      mirrorMat.opacity = 0.1 + Math.sin(Date.now() * 0.008) * 0.08;
    }

    // Rotate energy rings
    for (let i = 0; i < 3; i++) {
      const ring = this.mesh.getObjectByName(`energyRing${i}`);
      if (ring) {
        ring.rotation.y += delta * (1.5 + i * 0.5);
      }
    }

    return true;
  }

  takeDamage(amount: number): { remaining: number; reflected: number } {
    const reflected = Math.round(amount * this.reflectFraction);
    const absorbed = Math.min(this.health, amount);
    this.health -= absorbed;

    this.createHitEffect();

    if (this.health <= 0) {
      this.deactivate();
    }

    return { remaining: amount - absorbed, reflected };
  }

  private createHitEffect(): void {
    const flashMaterial = this.dome.material as THREE.MeshBasicMaterial;
    const originalOpacity = flashMaterial.opacity;
    flashMaterial.opacity = 0.9;

    if (this.hitEffectTimeout !== null) {
      clearTimeout(this.hitEffectTimeout);
    }
    this.hitEffectTimeout = setTimeout(() => {
      this.hitEffectTimeout = null;
      if (this.isActive) {
        flashMaterial.opacity = originalOpacity;
      }
    }, 100);

    // Golden ripple effect
    const rippleGeometry = new THREE.RingGeometry(0.5, 1.5, 32);
    const rippleMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFDD44,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const ripple = new THREE.Mesh(rippleGeometry, rippleMaterial);
    ripple.position.y = 0.8;
    ripple.rotation.x = -Math.PI / 2;
    this.mesh.add(ripple);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;
      ripple.scale.setScalar(1 + elapsed * 4);
      rippleMaterial.opacity = 0.9 - (elapsed / 0.3);

      if (elapsed < 0.3) {
        this.rippleAnimationId = requestAnimationFrame(animate);
      } else {
        this.rippleAnimationId = null;
        this.mesh.remove(ripple);
        rippleGeometry.dispose();
        rippleMaterial.dispose();
      }
    };
    this.rippleAnimationId = requestAnimationFrame(animate);
  }

  private deactivate(): void {
    this.isActive = false;
    this.createDissolveEffect();
  }

  private createDissolveEffect(): void {
    const dissolveParticleCount = 80;
    const positions = new Float32Array(dissolveParticleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < dissolveParticleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI / 2;
      const r = 2.2;

      positions[i * 3] = this.mesh.position.x + r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = this.mesh.position.y + r * Math.cos(phi);
      positions[i * 3 + 2] = this.mesh.position.z + r * Math.sin(phi) * Math.sin(theta);

      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 3,
        Math.random() * 4 + 1,
        (Math.random() - 0.5) * 3,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xFFD700,
      size: 0.25,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const dissolve = new THREE.Points(geometry, material);
    this.scene.add(dissolve);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = dissolve.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < dissolveParticleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
      }
      dissolve.geometry.attributes.position.needsUpdate = true;

      material.opacity = 1 - (elapsed / 0.8);

      if (elapsed < 0.8) {
        this.dissolveAnimationId = requestAnimationFrame(animate);
      } else {
        this.dissolveAnimationId = null;
        this.scene.remove(dissolve);
        geometry.dispose();
        material.dispose();
      }
    };

    this.dissolveAnimationId = requestAnimationFrame(animate);
    this.dispose();
  }

  getHealth(): number {
    return this.health;
  }

  getReflectFraction(): number {
    return this.reflectFraction;
  }

  isShieldActive(): boolean {
    return this.isActive;
  }

  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  setPosition(pos: THREE.Vector3): void {
    this.mesh.position.copy(pos);
  }

  dispose(): void {
    if (this.hitEffectTimeout !== null) {
      clearTimeout(this.hitEffectTimeout);
      this.hitEffectTimeout = null;
    }
    if (this.rippleAnimationId !== null) {
      cancelAnimationFrame(this.rippleAnimationId);
      this.rippleAnimationId = null;
    }
    if (this.dissolveAnimationId !== null) {
      cancelAnimationFrame(this.dissolveAnimationId);
      this.dissolveAnimationId = null;
    }
    this.scene.remove(this.mesh);
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
